import { useMutation } from "@tanstack/react-query";
import { Form, Flex, message, Alert } from "antd";
import { Button, Card, CardFooter, Input } from "@rumbledirect/ui";
import { Modal } from "@/components/v2";
import { createConceptInvite } from "@/lib/api/resources/dossierConcepts";

interface CreateConceptFormValues {
  firstName: string;
  subName?: string;
  lastName: string;
  email: string;
  telephone: string;
  licensePlate: string;
}

interface CreateConceptModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onCreated?: () => void;
}

export const CreateConceptModal = ({
  open,
  setOpen,
  onCreated,
}: CreateConceptModalProps) => {
  const [form] = Form.useForm<CreateConceptFormValues>();

  const createConceptMutation = useMutation({
    mutationFn: async (values: CreateConceptFormValues) => {
      return createConceptInvite({
        dossierType: "repair_direct.generic",
        label: "Uitnodiging " + values.licensePlate,
        note: null,
        data: {
          driver: {
            firstName: values.firstName,
            subName: values.subName ?? null,
            lastName: values.lastName,
            email: values.email,
            telephone: values.telephone,
          },
          licensePlate: {
            country: "NL",
            identifier: values.licensePlate,
          },

          // TODO make this a select dropdown
          affiliateUrl: "default",
        },
      });
    },
    onSuccess: () => {
      message.success("Concept succesvol aangemaakt");
      form.resetFields();
      setOpen(false);
      onCreated?.();
    },
    onError: () =>
      message.error(
        "Er is een fout opgetreden bij het aanmaken van het concept.",
      ),
  });

  return (
    <Modal
      open={open}
      onCancel={() => setOpen(false)}
      destroyOnClose
      afterClose={() => form.resetFields()}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={(values) => createConceptMutation.mutate(values)}
      >
        <Card title="Nieuw concept aanmaken">
          <Flex vertical gap={8}>
            <Form.Item
              label="Voornaam"
              name="firstName"
              rules={[{ required: true, message: "Dit veld is verplicht" }]}
            >
              <Input />
            </Form.Item>
            <Form.Item label="Tussenvoegsel" name="subName">
              <Input />
            </Form.Item>
            <Form.Item
              label="Achternaam"
              name="lastName"
              rules={[{ required: true, message: "Dit veld is verplicht" }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="E-mailadres"
              name="email"
              rules={[
                { required: true, message: "Dit veld is verplicht" },
                { type: "email", message: "Voer een geldig e-mailadres in" },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="Telefoonnummer"
              name="telephone"
              rules={[{ required: true, message: "Dit veld is verplicht" }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="Kenteken"
              name="licensePlate"
              rules={[{ required: true, message: "Dit veld is verplicht" }]}
            >
              <Input />
            </Form.Item>
            <Alert
              type="info"
              showIcon
              title="Bij het aanmaken ontvangt de bestuurder per e-mail een uitnodiging."
            />
          </Flex>
          <CardFooter>
            <Button
              type="primary"
              htmlType="submit"
              loading={createConceptMutation.isPending}
            >
              Aanmaken
            </Button>
          </CardFooter>
        </Card>
      </Form>
    </Modal>
  );
};
